/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useMemo } from 'react';
import { Tag } from 'antd';
import CommonTable from '../../../../components/CommonTable';
import {
  SunOutlined,
  ThunderboltOutlined,
  MoonOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import styles from './styles.module.css';
import { withTranslation } from 'react-i18next';
import ConsumptionCard from './ConsumptionCard';
import GenerationCard from './GenerationCard';
import {
  formatCurrency,
  NumberFormat,
} from '../../../../services/utils/commonUtils';
import container from '../../../../../public/images/container.png';

function LecoBill({ billData = {}, t }) {
  // Destructure safely with default empty arrays
  const consumption = billData?.bill?.billBreakDown?.consumption || [];
  const fixedCharges = billData?.bill?.billBreakDown?.fixedCharges || [];
  const taxes = billData?.bill?.billBreakDown?.taxes || [];
  const thisBillAmount = billData?.bill?.billBreakDown?.thisBillAmount || 0;
  const generation = billData?.bill?.billBreakDown?.generation || [];

  const broughtForwards = billData?.bill?.broughtForwards || [];
  const carryForwards = billData?.bill?.carryForwards || [];

  // Memoized totals
  const totalFixedCharges = useMemo(
    () => fixedCharges.reduce((sum, charge) => sum + (charge.amount || 0), 0),
    [fixedCharges],
  );

  const totalTaxAmount = useMemo(
    () => taxes.reduce((sum, tax) => sum + (tax.amount || 0), 0),
    [taxes],
  );

  const totalUsage = useMemo(
    () =>
      consumption.reduce(
        (sum, item) => sum + (item.type !== 'MaxDemand' ? item.units || 0 : 0),
        0,
      ),
    [consumption],
  );

  const totalAmount = useMemo(
    () => consumption.reduce((sum, item) => sum + (item.amount || 0), 0),
    [consumption],
  );

  const totalBroughtForward = useMemo(
    () => broughtForwards.reduce((sum, item) => sum + (item.units || 0), 0),
    [broughtForwards],
  );

  const totalCarryForward = useMemo(
    () => carryForwards.reduce((sum, item) => sum + (item.units || 0), 0),
    [carryForwards],
  );

  const hasSlabs = useMemo(
    () =>
      consumption.some((item) =>
        Object.prototype.hasOwnProperty.call(item, 'slabs'),
      ),
    [consumption],
  );

  // Prepare consumption data with fixed charges
  const consumptionData = useMemo(
    () =>
      consumption.map((item, index) => {
        const fixedCharge = fixedCharges.find(
          (fc) => fc.from === item.from && fc.to === item.to,
        );
        return {
          key: index,
          ...item,
          fixedCharge: fixedCharge?.amount || 0,
          fixedUnits: fixedCharge?.units || null,
          slabs: item.slabs || [],
        };
      }),
    [consumption, fixedCharges],
  );

  // Transform consumption for grouped table
  const transformToGroupedTableData = () => {
    const timePeriodMap = {
      T1: t('label_day_time_import'),
      T2: t('label_peak_time_import'),
      T3: t('label_off_peak_time_import'),
      MaxDemand: t('label_max_demand'),
    };

    const iconMap = {
      T1: <SunOutlined style={{ color: 'var(--soft-purple)' }} />,
      T2: <ThunderboltOutlined style={{ color: 'var(--soft-purple)' }} />,
      T3: <MoonOutlined style={{ color: 'var(--soft-purple)' }} />,
      MaxDemand: <DashboardOutlined style={{ color: 'var(--soft-purple)' }} />,
    };

    const colorMap = {
      T1: 'gold',
      T2: 'volcano',
      T3: 'green',
      MaxDemand: 'purple',
    };

    const groupedData = [];
    const dateGroups = {};

    consumption.forEach((item) => {
      const dateKey = `${item.from}-${item.to}`;
      if (!dateGroups[dateKey]) {
        dateGroups[dateKey] = { from: item.from, to: item.to, items: [] };
      }
      dateGroups[dateKey].items.push(item);
    });

    Object.values(dateGroups).forEach((group) => {
      const groupFixedTotal = fixedCharges
        .filter((fc) => fc.from === group.from && fc.to === group.to)
        .reduce((s, fc) => s + (fc.amount || 0), 0);

      // Date header row
      groupedData.push({
        key: `group-${group.from}-${group.to}`,
        isDateGroup: true,
        type: (
          <div className={styles.touContent}>
            <div className={styles.fontWeight700}>
              {t('label_fixed_charges')} : {formatCurrency(groupFixedTotal)}
            </div>
            <div className={styles.dateRange}>
              <Tag color="blue">{group.from}</Tag>
              <span className={styles.margin04}>{t('label_to')}</span>
              <Tag color="green">{group.to}</Tag>
            </div>
          </div>
        ),
      });

      // Items
      group.items.forEach((item, idx) => {
        const unitLabel =
          item.type === 'MaxDemand' ? t('label_kVA') : t('label_kWh');
        groupedData.push({
          key: `item-${group.from}-${group.to}-${idx}`,
          type: (
            <div className={styles.touItem}>
              <Tag color={colorMap[item.type] || 'default'}>
                {iconMap[item.type] || ''}
              </Tag>
              <span>{timePeriodMap[item.type] || item.type}</span>
            </div>
          ),
          units: `${NumberFormat(item.units)} ${unitLabel}`,
          rate: item.rate,
          amount: item.amount,
        });
      });
    });

    return groupedData;
  };

  const summary = {
    totalDays: `${billData?.bill?.noOfDays || 0} ${t('label_days')}`,
    totalFixedCharges: formatCurrency(totalFixedCharges),
    totalTax: formatCurrency(totalTaxAmount),
    totalAmount: formatCurrency(totalAmount),
    totalBill: formatCurrency(thisBillAmount),
    totalUnits: `${NumberFormat(totalUsage)} ${t('label_kWh')}`,
    totalBroughtForward: NumberFormat(totalBroughtForward),
    totalCarryForward: NumberFormat(totalCarryForward),
  };

  const consumptionsWithoutSlabs = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      align: 'left',
      onCell: (record) => {
        if (record.isDateGroup) {
          return { colSpan: 4 };
        }
        return { colSpan: 1 };
      },
      render: (value, record) => {
        if (record.isDateGroup) {
          return value;
        }
        return <div className={styles.rateCell}>{value}</div>;
      },
    },
    {
      title: 'Units',
      dataIndex: 'units',
      key: 'units',
      align: 'center',
      onCell: (record) => ({
        colSpan: record.isDateGroup || record.isSubtotal ? 0 : 1,
      }),
    },
    {
      title: t('label_rate_LKR'),
      dataIndex: 'rate',
      key: 'rate',
      align: 'right',
      onCell: (record) => ({
        colSpan: record.isDateGroup || record.isSubtotal ? 0 : 1,
      }),
      render: (text) => formatCurrency(text),
    },
    {
      title: t('label_amount_LKR'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      onCell: (record) => ({
        colSpan: record.isDateGroup ? 0 : 1,
      }),
      render: (text, record) =>
        record.isSubtotal ? (
          <div className={styles.fontWeight700}>
            {formatCurrency(record.amount)}
          </div>
        ) : (
          formatCurrency(text)
        ),
    },
  ];

  return (
    <div className={styles.contentMain}>
      {/* Summary Cards */}
      <div className={styles.summaryCards}>
        <div className={styles.summaryCard}>
          <div className={styles.cardLabel}>{t('label_PERIOD')}</div>
          <div className={styles.cardValue}>{summary.totalDays}</div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.cardLabel}>{t('label_TOTAL_UNITS')}</div>
          <div className={styles.cardValue}>{summary.totalUnits}</div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.cardLabel}>{t('label_CONSUMPTION')}</div>
          <div className={styles.cardValue}>{summary.totalAmount}</div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.cardLabel}>{t('label_FIXED_CHARGES')}</div>
          <div className={styles.cardValue}>{summary.totalFixedCharges}</div>
        </div>

        {totalBroughtForward > 0 && (
          <div className={styles.summaryCard}>
            <div className={styles.cardLabel}>{t('label_brought_forward')}</div>
            <div className={styles.cardValue}>
              {summary.totalBroughtForward}
            </div>
          </div>
        )}

        {totalCarryForward > 0 && (
          <div className={styles.summaryCard}>
            <div className={styles.cardLabel}>{t('label_carry_forward')}</div>
            <div className={styles.cardValue}>{summary.totalCarryForward}</div>
          </div>
        )}

        <div className={styles.summaryCard}>
          <div className={styles.cardLabel}>{t('label_TOTAL_TAX')}</div>
          <div className={styles.cardValue}>{summary.totalTax}</div>
        </div>

        <div className={`${styles.summaryCard} ${styles.totalAmountCard}`}>
          <div className={`${styles.cardLabel} ${styles.totalAmountTextColor}`}>
            {t('label_TOTAL_AMOUNT')}
          </div>
          <div className={styles.cardValue}>{summary.totalBill}</div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className={styles.detailedBreakdownSection}>
        {consumption.length > 0 && (
          <div className={styles.breakdownContent}>
            {hasSlabs ? (
              <ConsumptionCard consumptionData={consumptionData} t={t} />
            ) : (
              <div className={styles.consumptionCardContainer}>
                <div className={styles.cardHeaderNew}>
                  <div className={styles.cardHeaderLabel}>
                    {t('label_consumption')}
                  </div>
                  <div>
                    <img src={container} alt="container icon" />
                  </div>
                </div>
                <CommonTable
                  columns={consumptionsWithoutSlabs}
                  dataSource={transformToGroupedTableData()}
                  rowClassName={(record) =>
                    record.isDateGroup ? styles.dateGroupRow : styles.compactRow
                  }
                />
              </div>
            )}
          </div>
        )}

        {generation.length > 0 && (
          <div className={styles.breakdownContent}>
            {generation.map((item, index) => (
              <GenerationCard key={index} item={item} t={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default withTranslation()(LecoBill);
